/**
 * Runtime shape checks for the wire.
 *
 * These are the only things standing between a backend contract change and a
 * confusing `undefined` three layers up, so they live with the contract rather
 * than in the transport.
 */
import type { LaravelEnvelope } from "./api-response";
import type { ValidationErrors } from "./api-error.types";
import type { PaginatedResponse } from "../pagination";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Is this Laravel's `{ data, message, code, pagination_data }`?
 *
 * `pagination_data` is deliberately *not* required: it is meaningless on a
 * non-list route, and demanding it would make the guard brittle if the backend
 * ever drops the key there.
 */
export function isLaravelEnvelope<TData = unknown>(
  value: unknown
): value is LaravelEnvelope<TData> {
  return (
    isRecord(value) &&
    "data" in value &&
    typeof value.message === "string" &&
    typeof value.code === "number"
  );
}

/** Does `data` look like a 422's `{ field: [messages] }` map? */
export function isValidationErrors(value: unknown): value is ValidationErrors {
  if (!isRecord(value)) return false;
  const entries = Object.values(value);
  return (
    entries.length > 0 &&
    entries.every(
      (messages) =>
        Array.isArray(messages) &&
        messages.every((message) => typeof message === "string")
    )
  );
}

/**
 * Is this the normalized `{ items, meta }` pair?
 *
 * Note this checks the shape the **provider produces**, not the shape Laravel
 * sends — on the wire a paginated payload is a bare array with the meta in a
 * sibling key.
 */
export function isPaginatedResponse<T>(
  value: unknown
): value is PaginatedResponse<T> {
  return isRecord(value) && Array.isArray(value.items) && isRecord(value.meta);
}
